import cart from 'lib/cart';
import bus from 'lib/bus';
import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import {
  encodeObjectID,
  addCloseListener,
  clearAllCloseListeners,
  decodeIDProductVariant,
  handleize,
  convertGQLToProductJson,
  htmlDecode,
  encodeProperties,
} from 'utils/general';
import {
  trackKlavioAddToCart,
  buildItemTrackingData,
  trackEvent,
  trackAddToCart,
} from 'lib/tracking';
import {
  formatPrice,
  formatPriceString,
  getAllValueForTag,
} from '../../utils/general';

/**
 * @info
 *
 * *The "store/cart" directory*
 * This "store" directory declares each of the Vuex
 * modules that all together power the site. Here, we have the
 * Vuex store module that sits behind cart functionality.
 *
 * *This file*
 * This file outlines "actions". Actions are functions that
 * include logic that, when ready, commits a mutation to the
 * application state. Whereas mutations must be synchronous,
 * actions return a promise and can include asynchronous
 * logical sequences.
 */

const closeCartActions = ({ commit }) => {
  commit('closeCart');
  clearAllCloseListeners();
};

export default {
  getCart({ commit, dispatch }, payload) {
    commit('setLoading', true);

    return cart.get().then((data) => {
      commit('global/refreshCart', data, { root: true });
      commit('setLoading', false);
      dispatch('hydrateProductsOnCart');
      dispatch('addItemComponentsToCart');
    });
  },

  refreshCart({ commit, dispatch }, data) {
    commit('global/refreshCart', data, { root: true });
    dispatch('hydrateProductsOnCart');
    dispatch('addItemComponentsToCart');
    bus.$emit('cart-refreshed');
  },

  hydrateProductsOnCart({ commit, rootState, state }) {
    const items = rootState.global?.cart?.items ?? [];

    // Force Reset the Cart Data
    commit('resetProductCartData');

    if (!items || !items.length) return;
    items.forEach(async (item) => {
      const productHandle = item.handle;
      const collectionHandle = handleize(
        item.product_title.split(__GLOBAL__.productDelimiter)[0],
      );
      const quantity = item.quantity;

      // Skip if already fetched once
      if (
        typeof state.productsData[productHandle] !== 'undefined' ||
        typeof state.productsData[collectionHandle] !== 'undefined'
      ) {
        return;
      }

      // Microcollection
      await storefront
        .request(queries.collectionWithVariants, { handle: collectionHandle })
        .then(async (response) => {
          const collection = response.collection?.products?.edges ?? null;

          const handle = collection ? collectionHandle : productHandle;

          commit('updateProductCartData', {
            handle,
            productHandle,
            data: {
              collection,
            },
          });

          // Product object (needed for variants)
          await storefront
            .request(queries.quickViewProduct, { handle: productHandle })
            .then((response) => {
              const product = {
                ...response.product,
                images: response.product?.images?.edges,
                variants: response.product?.variants?.edges,
                quantity,
              };
              commit('updateProductCartData', {
                handle,
                productHandle,
                data: product,
              });
            })
            .catch((error) => {
              console.error(error);
            });
        })
        .catch((error) => {
          console.error(error);
        });
    });
  },

  addItemComponentsToCart({ commit, rootState, state }) {
    const items = rootState.global?.cart?.items ?? [];
    if (!items || !items.length) return;

    commit('resetCartComponents');

    return fetch(`${Shopify.routes.root}cart?view=components`)
      .then((response) => {
        return response.json();
      })
      .then((jsonData) => {
        commit('updateCartComponents', {
          components: jsonData,
        });
      });
  },

  updateCartItem(
    { commit, dispatch, rootGetters },
    {
      quantity,
      id = null,
      variantId = null,
      isBundle = false,
      properties = false,
      callback = () => {},
      action = 'add',
      dontStopLoading = true,
      subscription = undefined,
      isEditable = false,
    },
  ) {
    if (!id && !variantId) return;

    if (!id) {
      const item = rootGetters['global/cartLineByVariantId']({
        id: variantId,
        isBundle,
        properties,
        isEditable,
      });

      if (item) {
        action = 'change';
        id = item.key;
        subscription =
          item?.selling_plan_allocation?.selling_plan?.id || undefined;
      } else {
        id = variantId;
        action = 'add';
      }
    }

    const updateState = (data) => {
      dispatch('refreshCart', data);
      if (!dontStopLoading) {
        commit('setLoading', false);
      }
      callback(subscription);
    };

    if (action === 'change') {
      const payload = { quantity, id };
      if (subscription) {
        payload['subscription'] = subscription;
      }

      return cart.change(payload).then(updateState);
    } else {
      const payload = {
        quantity,
        id,
        properties: encodeProperties(properties),
      };
      if (subscription) {
        payload['subscription'] = subscription;
      }
      return cart.add(payload).then(() => {
        cart.get().then(updateState);
      });
    }
  },

  openCart({ commit }) {
    commit('setCart', true);
    addCloseListener(() => {
      closeCartActions({ commit });
    });
  },

  closeCart({ commit }) {
    closeCartActions({ commit });
  },

  toggleCart({ commit, state }) {
    commit('toggleCart');

    if (state.open === true) {

      if (typeof window.narvarCartWidgetReload === 'function') {
        setTimeout(() => {
          window.narvarCartWidgetReload();
        }, 100);
      }

      addCloseListener(() => {
        closeCartActions({ commit });
      });
    } else {
      clearAllCloseListeners();
    }
  },

  updateCartLine(
    { dispatch },
    { quantity, id, callback = () => {}, errorCallback = () => {} },
  ) {
    cart
      .change({ quantity, id })
      .then((data) => {
        dispatch('refreshCart', data);
        callback(data);
        if (data.errors && data.errors.length) {
          errorCallback?.(data.errors[0]);
        }
      })
      .catch((error) => {
        console.log('Error updating cart line:', error);
        errorCallback?.(error);
      });
  },

  updateBundleCartItems(
    { commit, dispatch, rootGetters },
    { quantity, items, callback = () => {} },
  ) {
    commit('setLoading', 'cartBundles');
    let updates = {};
    items.forEach((item) => {
      updates[item.variant_id] = quantity;
    });

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch('refreshCart', data);
        commit('setLoading', false);
        callback();
      });
  },

  addBundleItemsToCart(
    { commit, dispatch, rootGetters, rootState },
    {
      lines,
      quantity = 1,
      bundleProduct,
      bundles = [],
      callback = () => {},
      otherData,
    },
  ) {
    commit('setLoading', 'cartBundles');

    if (bundles.length) {
      let tempData = {
        ...otherData,
        bundleName:
          `${
            bundleProduct.type == 'Bundle Builder' ? 'bundle builder: ' : ''
          }` + htmlDecode(bundleProduct.title),
        bundleId: String(bundleProduct.id),
      };
      const items = bundles.map((product, idx) => {
        const lineData = {
          ...tempData,
          ...(otherData.bundleType == 'bundle builder' && {
            origin: lines[idx].properties._origin,
            originProduct: lines[idx].properties._origin_product,
            listName: lines[idx].properties._item_list_name,
            listId: lines[idx].properties._item_list_id,
            index: parseInt(lines[idx].properties._index),
            discount: otherData.discount,
          }),
        };
        return buildItemTrackingData(
          {
            ...product,
            isBundle: true,
            tags: bundleProduct.tags,
          },
          quantity,
          lineData,
        );
      });
      const eventData = {
        event: 'add_to_cart',
        ...(otherData.siteSection && {
          site_section: otherData.siteSection,
        }),
        ...(otherData.componentName && {
          component_name: otherData.componentName,
        }),
        ...(otherData.bundleType && {
          bundle_type: otherData.bundleType,
        }),
        ecommerce: {
          currency: window.Shopify.currency.active,
          value: Number(formatPriceString(bundleProduct.price / 100)),
          bundle_name: tempData.bundleName,
          bundle_id: tempData.bundleId,
          items,
        },
      };
      trackEvent(eventData);
    }

    const addToCart = (i = 0) => {
      const isLast = !(i < lines.length - 1);
      dispatch('addToCart', {
        ...lines[i],
        dontStopLoading: !isLast,
        isBundle: true,
        callback: () => {
          if (!isLast) {
            addToCart(i + 1);
          } else {
            callback();
          }
        },
      });
    };

    if (otherData.bundleType == 'virtual bundle') {
      dispatch('addVirtualBundleToCart', {
        bundleProduct,
        lines,
        quantity,
        bundles,
        callback,
        otherData,
      });
    } else {
      addToCart(0);
    }
  },

  addVirtualBundleToCart(
    { dispatch },
    { bundleProduct, callback, lines, quantity, otherData },
  ) {
    const child_data = lines.map((line) => {
      return {
        variant: line.id,
        parent: bundleProduct.id,
        ...(line.properties?._bundle_hash && {
          hash: line.properties?._bundle_hash,
        }),
        original_price: formatPrice(
          line.properties?._bundle_product_price,
          true,
          window.Shopify.currency.active,
        ),
        price_cents: parseInt(line.properties?._bundle_product_price, 10),
        currency: window.Shopify.currency.active,
        // properties: {
        //   ...line.properties,
        //   _key: 'value',
        //   _kitId: bundleProduct.id,
        // },
      };
    });

    dispatch('addToCart', {
      variant: bundleProduct.variants[0],
      quantity: quantity,
      properties: {
        ...(lines[0]?.properties?._bundle_uid && {
          _bundle_uid: lines[0].properties._bundle_uid,
        }),
        ...(otherData?.bundleFullPrice && {
          _bundle_full_price: otherData.bundleFullPrice,
        }),
        _bundle_price: bundleProduct.bundle_value,
        _bundle: JSON.stringify(child_data),
        _handles: JSON.stringify(
          lines.map((line) => line.properties._product_handle),
        ),
        _bundle_type: 'virtual bundle',
      },
      callback: () => {
        callback?.();
      },
      isBundle: true,
    });
  },

  async addToCart(
    { dispatch, commit, rootGetters, rootState },
    {
      variant = null,
      id,
      quantity = 1,
      properties = null,
      callback = () => {},
      dontStopLoading = false,
      isBundle = false,
      subscription = undefined,
      moduleName = null,
    },
  ) {
    commit('setCartInProgress', true);

    properties = properties ?? {};
    id = id ?? variant?.id;

    // Decode ID
    if (`${id}`.includes('=') || `${id}`.includes('ProductVariant')) {
      id = decodeIDProductVariant(id);
    }

    const globalVariantId = encodeObjectID(id, 'ProductVariant');
    let product = rootState?.product.product;

    if (!rootState?.product.isStatic) {
      product = await storefront
        .request(queries.productTagByVariantId, { id: globalVariantId })
        .then((response) => {
          return Promise.resolve(
            convertGQLToProductJson(response.node.product),
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }

    // If necessary, get replacement product
    const replacementTags = (product?.tags ?? []).filter((tag) =>
      tag.includes(`shipping-restricted-replacement|`),
    );
    if (replacementTags.length) {
      const restrictedCodes = replacementTags.map((rt) => rt.split('|')[1]);
      const shippingCodes = window.shippingCodes.filter(
        (sc) => !restrictedCodes.includes(sc),
      );
      properties['_restricted'] = shippingCodes.join('|');
    }
    const replacementTag = (replacementTags ?? []).find((tag) =>
      tag.includes(`shipping-restricted-replacement|${window.shippingCode}|`),
    );
    const replacementId = replacementTag?.split('|').pop() || null;
    if (replacementId) {
      id = parseInt(replacementId, 10);
      properties['_restricted'] = window.shippingCode;
      properties['_url'] = `${Shopify.routes.root}products/${product.handle}`;

      const replacementGlobalId = encodeObjectID(
        replacementId,
        'ProductVariant',
      );

      product = await storefront
        .request(queries.productTagByVariantId, {
          id: replacementGlobalId,
        })
        .then((response) => {
          return Promise.resolve(
            convertGQLToProductJson(response.node.product),
          );
        })
        .catch((error) => {
          console.error(error);
        });
    }

    // Adds the requested `quantity` to the current item.quantity
    let item = rootGetters['global/cartLineByVariantId']({
      id,
      properties,
    });

    if (!properties._gwp && item && item.properties?._gwp) {
      item = null;
    }

    const totalQuantity = item
      ? parseInt(item.quantity) + parseInt(quantity)
      : parseInt(quantity);

    if (subscription) {
      properties['_subscription'] = subscription;
      // on markdown property
      properties['_omd'] = variant?.compareAtPrice ? true : false;
    }

    if (!properties._item_list_id || !properties._item_list_name) {
      //collection or search page
      properties['_item_list_id'] =
        window.searchspring?.controller?.search?.context?.collection?.handle ||
        'search';
      properties['_item_list_name'] =
        htmlDecode(
          window.searchspring?.controller?.search?.context?.collection?.name,
        ) || 'Search';
    }

    if (!properties._origin || !properties._origin_product) {
      //collection or search page
      properties['_origin'] = document.body.dataset.origin;
      properties['_origin_product'] = htmlDecode(
        document.body.dataset.originProduct,
      );
    }

    properties['_product_available'] = product.available;
    const productTags = getAllValueForTag('badge', product.tags);
    const encodedTags =
      Array.isArray(productTags) && productTags.length
        ? productTags.map((tag) => encodeURIComponent(tag))
        : encodeURIComponent(productTags);
    properties['_product_context'] = encodedTags;

    if (!isBundle) {
      trackKlavioAddToCart({ product, variant });

      const otherData = {
        origin: properties._origin,
        originProduct: properties._origin_product,
        listName: properties._item_list_name,
        listId: properties._item_list_id,
        subFrequency: properties._subscription_frequency,
        frequency: properties._subscription_frequency,
        index: parseInt(properties._index),
        moduleName,
      };

      if (variant) {
        otherData['price'] = Number(formatPriceString(variant.price / 100));
      }

      trackAddToCart({
        product,
        quantity,
        otherData,
      });
    }

    commit('setCart', true);
    let action = item ? 'change' : 'add';
    let _id = item?.key ?? id;
    let _quantity = totalQuantity;
    if (properties._gwp) {
      action = 'add';
      _id = id;
      _quantity = 1;
    }
    return dispatch('updateCartItem', {
      id: _id,
      quantity: _quantity,
      properties,
      dontStopLoading,
      action,
      subscription,
      isBundle,
      callback: () => {
        commit('setCartInProgress', false);
        if (callback) {
          callback();
        }
      },
    });
  },

  addMultipleItemsToCart(
    { commit, dispatch, rootGetters, rootState },
    { lines, quantity = 1, callback = () => {}, otherData },
  ) {
    const addToCart = (i = 0) => {
      const isLast = !(i < lines.length - 1);
      dispatch('addToCart', {
        ...lines[i],
        dontStopLoading: !isLast,
        callback: () => {
          if (!isLast) {
            addToCart(i + 1);
          } else {
            callback();
          }

          const item = buildItemTrackingData({ ...lines[i].product }, 1, {
            ...otherData,
          });

          const eventData = {
            event: 'add_to_cart',
            ecommerce: {
              currency: window.Shopify.currency.active,
              value: Number(item.price),
              items: item,
            },
          };
          trackEvent(eventData);
        },
      });
    };
    addToCart(0);
  },

  addMultipleToCart({ dispatch, commit, state }, array) {
    cart.multiple({ array }).then((data) => {
      dispatch('getCart');
      this.dispatch('global/closeModal');
      this.dispatch('global/clearUserSelection');
      this.dispatch('global/setGWPLoading', false);
    });
  },
  // This function was created for the sole purpose of adding multiple products to the cart
  // and avoid some extra events for the promo codes feature
  // DO NOT USE THIS FUNCTION
  addMultipleCleanToCart(
    { dispatch, commit, state },
    { openCart = true, products },
  ) {
    commit('setCouponLoading', true);
    if (products?.length) {
      cart.cleanMultiple({ array: products }).then((_) => {
        cart.get().then((data) => {
          commit('global/refreshCart', data, { root: true });
          openCart && dispatch('openCart');
          commit('setCouponLoading', false);
        });
      });
    }
  },
  updateChangePending({ commit }, value) {
    commit('setChangePending', value);
  },
  updateCartInProgress({ commit }, value) {
    commit('setCartInProgress', value);
  },
};
